import { createAsyncThunk } from '@reduxjs/toolkit';
import { PatientsActions } from '../../../constants/actions';
import { AxiosError } from 'axios';
import { PatientsApi } from 'integration/api/patients';
import {
  FamilyMemberCreationDto,
  FamilyMemberUpdateDto,
  PatientBriefCreationDto,
} from '@docbay/schemas';
import { PatientsUpdateProps } from './models';
import { FetchPatientsParams } from 'common/types/patients';

/**
 * Fetches patients based on the provided parameters.
 * @param params - Parameters for fetching patients.
 * @returns An array of patient data or an error message if the request fails.
 */
export const fetchPatients = createAsyncThunk(
  PatientsActions.fetchPatients,
  async (params: FetchPatientsParams, { rejectWithValue }) => {
    try {
      const { data: responseData } = await PatientsApi.fetchPatients(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches patients and their family members based on the provided parameters.
 * @param params - Parameters for fetching patients and family members.
 * @returns An array of patient and family member data or an error message if the request fails.
 */
export const fetchPatientsAndFamilyMembers = createAsyncThunk(
  PatientsActions.fetchPatientsAndFamilyMembers,
  async (params: { search: string }, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await PatientsApi.fetchPatientsAndFamilyMembers(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Creates a new patient.
 * @param params - Data for creating the patient.
 * @returns The created patient data or an error message if the request fails.
 */
export const createPatient = createAsyncThunk(
  PatientsActions.createPatient,
  async (params: PatientBriefCreationDto, { rejectWithValue }) => {
    try {
      const { data: responseData } = await PatientsApi.createPatients(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Updates an existing patient.
 * @param props - The ID of the patient to update and the data to update with.
 * @returns The updated patient data or an object containing the status code and error message if the request fails.
 */
export const updatePatient = createAsyncThunk(
  PatientsActions.updatePatients,
  async ({ id, data }: PatientsUpdateProps, { rejectWithValue }) => {
    try {
      const { data: responseData } = await PatientsApi.updatePatients(id, data);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;

      const rejectPayload = {
        statusCode: error?.response?.status,
        message: error.message,
      };
      return rejectWithValue(rejectPayload);
    }
  },
);

/**
 * Deletes an existing patient.
 * @param id - The ID of the patient to delete.
 * @returns An empty object or an error message if the request fails. Fetches patients after deletion.
 */
export const deletePatient = createAsyncThunk(
  PatientsActions.deletePatient,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await PatientsApi.deletePatient(id);
      fetchPatients({ page: 1, limit: 10 });
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Gets a patient by their ID.
 * @param id - The ID of the patient to get.
 * @returns The patient data or an object containing the status code and error message if the request fails.
 */
export const getPatientById = createAsyncThunk(
  `patients/${PatientsActions.getPatientById}`,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await PatientsApi.getPatientById(id);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      const rejectPayload = {
        statusCode: error?.response?.status,
        message: error.message,
      };
      return rejectWithValue(rejectPayload);
    }
  },
);

/**
 * Gets the subscriptions of a patient.
 * @param id - The ID of the patient to get subscriptions for.
 * @returns An array of subscription data or an error message if the request fails.
 */
export const getPatientsSubscriptions = createAsyncThunk(
  `patients/${PatientsActions.getPatientsSubscriptions}`,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await PatientsApi.getPatientsSubscriptions(
        id,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Gets the family members of a patient.
 * @param id - The ID of the patient to get family members for.
 * @returns An array of family member data or an error message if the request fails.
 */
export const getFamilyMembers = createAsyncThunk(
  `patients/${PatientsActions.getFamilyMembers}`,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await PatientsApi.getFamilyMembers(id);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Creates a new family member for a patient.
 * @param props - The ID of the patient and the data for creating the family member.
 * @returns The created family member data or an error message if the request fails.
 */
export const createFamilyMember = createAsyncThunk(
  `patients/${PatientsActions.createFamilyMember}`,
  async (
    {
      patientId,
      data,
    }: {
      patientId: string;
      data: FamilyMemberCreationDto;
    },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } = await PatientsApi.createFamilyMember(
        patientId,
        data,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes an existing family member for a patient.
 * @param props - The ID of the patient and the ID of the family member to delete.
 * @returns An empty object or an error message if the request fails.
 */
export const deleteFamilyMember = createAsyncThunk(
  `patients/${PatientsActions.deleteFamilyMember}`,
  async (
    { patientId, fmId }: { patientId: string; fmId: string },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } = await PatientsApi.deleteFamilyMember(
        patientId,
        fmId,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Updates an existing family member for a patient.
 * @param props - The ID of the patient, the ID of the family member to update, and the data to update with.
 * @returns The updated family member data or an error message if the request fails.
 */
export const updateFamilyMember = createAsyncThunk(
  `patients/${PatientsActions.updateFamilyMember}`,
  async (
    {
      patientId,
      data,
      memberId,
    }: { patientId: string; memberId: string; data: FamilyMemberUpdateDto },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } = await PatientsApi.updateFamilyMember(
        patientId,
        memberId,
        data,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Uploads a CSV file containing patient data.
 * @param data - The FormData object containing the CSV file to upload.
 * @returns An empty object or an error message if the request fails.
 */
export const uploadCSV = createAsyncThunk(
  PatientsActions.uploadCSV,
  async (data: FormData, { rejectWithValue }) => {
    try {
      const { data: responseData } = await PatientsApi.uploadCSV(data);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
